import _ from 'lodash';
import moment from 'moment';
import pluralize from 'pluralize';
import LocalizedStrings from 'react-localization';
import 'moment/locale/en-gb';
// enable other translation
// import 'moment/locale/zh-cn';
// import 'moment/locale/zh-hk';

import availableLocales from '@locale/available-locales';

type Paths<T> = T extends object ? { [K in keyof T]:
  `${Exclude<K, symbol>}${'' | `.${Paths<T[K]>}`}`
}[keyof T] : never;

type Translation<T, V extends keyof T | Paths<T> | string> = V extends keyof T ?
  T[V] extends string ? string :
    T[V] extends any[] ? T[V] : {
      [K in keyof T[V]]: Translation<T[V], K>
    } : V extends `${infer Key}.${infer SubKey}` ?
    Key extends keyof T ? Translation<T[Key], SubKey>
      : string : string;

export type TranslationKey = Paths<typeof availableLocales.en>;

const fallbackLocale = 'en-gb';
const locale = new LocalizedStrings(availableLocales);

const parseTranslation = (text: string, data: any) => {
  if (!data) return text;

  let parsedText = text;

  Object.entries(data).forEach(([ key, value ]: any) => {
    // disable temporary since it shows object object when combined with text
    // if (typeof value === 'function') {
    //   parsedText = parseTranslationElement(parsedText, key, value);
    // }

    parsedText = parsedText.replace(key, value);
  });
  // expected return is array to also support element transition
  return parsedText;
};

// this is the new parse translation function, need to update the existing parseTranslations
// and parseElementTranslation into this function
const newParseTranslation = (text: string, ...values: any[]) => locale.formatString(text.replace(/:(\w)+/g, match => `{${match.replace(':', '')}}`), ...values) as string;

const getTranslations = <T extends Paths<typeof availableLocales.en>>
  (key: T) => _.get(locale, key) as unknown as Translation<typeof availableLocales.en, T>;

const getPluralizedTranslation = (
  word: string,
  count: number,
  inclusive = true,
) => pluralize(word, count, inclusive);

const setLanguage = (language: string) => {
  // translate date and time
  const languageCode = language === fallbackLocale ? fallbackLocale : language.substring(0, 2);
  let momentLocale = moment.locales().find(e => e.startsWith(languageCode));
  momentLocale = momentLocale === 'en' ? fallbackLocale : momentLocale;
  moment.locale(momentLocale || fallbackLocale);

  // translate texts
  locale.setLanguage(language);
};

// flat parsing
const parseTranslationElement = (text: any, data: any) => {
  if (!data) return text;

  // create a regexp first
  const splitRegExString: string[] = [];
  const expectedStrings: Record<string, any> = {};
  Object.entries(data).forEach(([ key, value ]) => {
    // eslint-disable-next-line no-useless-escape
    const regExString = `<${key}>(.*?)<\/${key}>`;
    // push the split regex
    splitRegExString.push(regExString);

    // then get the child text to convert it later
    const regEx = new RegExp(regExString, 'g');
    const foundText = text.match(regEx);

    if (!foundText) return;

    // if there is 2 text with same element loop through them
    foundText.map((foundTextValue: string) => {
      // child text inside the element tag
      let childText: any = foundTextValue.replace(`<${key}>`, '');
      childText = childText.replace(`</${key}>`, '');

      // after getting the child text store the key as temp in the arr
      // and the callback provided
      expectedStrings[childText] = value;

      return foundTextValue;
    });
  });

  const splitRegex = new RegExp(splitRegExString.join('|'), 'g');
  const splitText = text.split(splitRegex);

  return splitText.map((convertedText: string) => {
    if (expectedStrings[convertedText]) {
      return expectedStrings[convertedText](convertedText);
    }

    return convertedText;
  });
};

const getLanguage = () => locale.getLanguage();

export default {
  getTranslations,
  parseTranslation,
  // update all translations into this function
  newParseTranslation,
  getPluralizedTranslation,
  setLanguage,
  getLanguage,
  parseTranslationElement,
};
